import React from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  FormControl,
  FormLabel,
  HStack,
  ListItem,
  SimpleGrid,
  Select,
  Spinner,
  Text,
  UnorderedList,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import riskReviewService from "../../services/riskReviewService";

const getWorkflowId = (workflow) =>
  workflow?.workflowId?._id ||
  workflow?.workflowId ||
  workflow?._id ||
  "";

const buildOptionKey = (workflow) =>
  workflow?.optionKey || `${getWorkflowId(workflow)}:v${workflow?.version || 1}`;

const getRequestWorkflowKey = (request) => {
  const workflowId = request?.workflowId?._id || request?.workflowId;
  const workflowVersion =
    request?.workflowVersion || request?.workflowSnapshot?.version || request?.workflowId?.version || 1;

  return workflowId ? `${workflowId}:v${workflowVersion}` : "";
};

const buildPersistedWorkflowOption = (request) => {
  const optionKey = getRequestWorkflowKey(request);
  if (!optionKey) return null;

  return {
    optionKey,
    workflowId: request?.workflowId?._id || request?.workflowId,
    name: request?.workflowSnapshot?.name || request?.workflowId?.name || "Selected workflow",
    version: request?.workflowVersion || request?.workflowSnapshot?.version || request?.workflowId?.version || 1,
    description: request?.workflowSnapshot?.description || request?.workflowId?.description,
    stages: request?.workflowSnapshot?.stages || request?.workflowId?.stages || [],
    isActive: request?.workflowId?.isActive,
  };
};

const getStageApproverSummary = (stage) => {
  if (stage.approverType === "USER") {
    const count = Array.isArray(stage.approverUsers) ? stage.approverUsers.length : 0;
    return count > 1 ? `${count} selected users` : "Selected user";
  }
  return stage.approverRole || stage.approverType || "Approver";
};

const RiskReviewWorkflowSelection = ({ request, selectedWorkflowId, onChange, disabled }) => {
  const toast = useToast();
  const panelBg = useColorModeValue("blue.50", "whiteAlpha.50");
  const borderColor = useColorModeValue("blue.200", "blue.700");
  const previewBg = useColorModeValue("white", "gray.800");
  const [workflowLoading, setWorkflowLoading] = React.useState(false);
  const [workflowOptions, setWorkflowOptions] = React.useState([]);
  const [resolvedCriteria, setResolvedCriteria] = React.useState(null);
  const [criteria, setCriteria] = React.useState({
    unit: "",
    department: "",
    branch: "",
  });
  const [criteriaOptions, setCriteriaOptions] = React.useState({
    units: [],
    departments: [],
    branches: [],
    combinations: [],
  });

  React.useEffect(() => {
    const requestCriteria = {
      unit: request?.businessUnit || "",
      department: request?.division || "",
      branch: request?.country || "",
    };
    const hasRequestCriteria =
      requestCriteria.unit || requestCriteria.department || requestCriteria.branch;

    if (!hasRequestCriteria) return;

    setCriteria((current) => {
      if (
        current.unit === requestCriteria.unit &&
        current.department === requestCriteria.department &&
        current.branch === requestCriteria.branch
      ) {
        return current;
      }

      return requestCriteria;
    });
  }, [request?.businessUnit, request?.country, request?.division]);

  React.useEffect(() => {
    const requestWorkflowKey = getRequestWorkflowKey(request);
    if (requestWorkflowKey) onChange?.(requestWorkflowKey);
  }, [onChange, request?.workflowId, request?.workflowSnapshot?.version, request?.workflowVersion]);

  React.useEffect(() => {
    let cancelled = false;

    const loadCriteriaOptions = async () => {
      try {
        const data = await riskReviewService.getWorkflowCriteriaOptions();
        if (cancelled) return;

        setCriteriaOptions({
          units: Array.isArray(data?.units) ? data.units : [],
          departments: Array.isArray(data?.departments) ? data.departments : [],
          branches: Array.isArray(data?.branches) ? data.branches : [],
          combinations: Array.isArray(data?.combinations) ? data.combinations : [],
        });
      } catch (error) {
        if (cancelled) return;
        setCriteriaOptions({ units: [], departments: [], branches: [], combinations: [] });
        toast({
          title: "Workflow criteria unavailable",
          description:
            error?.response?.data?.message ||
            "Unable to load IOM workflow setup criteria.",
          status: "warning",
        });
      }
    };

    loadCriteriaOptions();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const updateCriteria = (key, value) => {
    setCriteria((current) => ({ ...current, [key]: value }));
    setWorkflowOptions([]);
    setResolvedCriteria(null);
    onChange?.("");
  };

  React.useEffect(() => {
    let cancelled = false;

    const loadWorkflowVersions = async () => {
      const requestWorkflowId = request?.workflowId?._id || request?.workflowId || "";
      const requestWorkflowVersion =
        request?.workflowVersion || request?.workflowSnapshot?.version || request?.workflowId?.version || 1;

      if (!requestWorkflowId && (!criteria.unit || !criteria.department || !criteria.branch)) {
        setWorkflowOptions([]);
        setResolvedCriteria(null);
        onChange?.("");
        return;
      }

      try {
        setWorkflowLoading(true);
        const data = await riskReviewService.getWorkflowVersions({
          businessUnit: criteria.unit,
          division: criteria.department,
          country: criteria.branch,
          workflowId: requestWorkflowId,
          workflowVersion: requestWorkflowId ? requestWorkflowVersion : undefined,
        });
        const workflows = Array.isArray(data?.workflows) ? data.workflows : [];
        if (cancelled) return;

        setWorkflowOptions(workflows);
        setResolvedCriteria(data?.resolvedCriteria || criteria);

        const existingKey = getRequestWorkflowKey(request);
        const nextSelected =
          workflows.find((workflow) => buildOptionKey(workflow) === existingKey)?.optionKey ||
          existingKey ||
          "";

        onChange?.(nextSelected);
      } catch (error) {
        if (cancelled) return;
        setWorkflowOptions([]);
        setResolvedCriteria(criteria);
        onChange?.("");
        toast({
          title: "Workflow not resolved",
          description:
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            "No workflow version found for this Risk Review criteria.",
          status: "warning",
        });
      } finally {
        if (!cancelled) setWorkflowLoading(false);
      }
    };

    loadWorkflowVersions();
    return () => {
      cancelled = true;
    };
  }, [criteria, onChange, request?.workflowId, request?.workflowSnapshot?.version, request?.workflowVersion, toast]);

  const persistedWorkflowOption = buildPersistedWorkflowOption(request);
  const workflowOptionsToShow =
    persistedWorkflowOption &&
    !workflowOptions.some(
      (workflow) => buildOptionKey(workflow) === persistedWorkflowOption.optionKey,
    )
      ? [persistedWorkflowOption, ...workflowOptions]
      : workflowOptions;
  const selectedWorkflow = workflowOptionsToShow.find(
    (workflow) => buildOptionKey(workflow) === selectedWorkflowId,
  );
  const approvalStages = selectedWorkflow?.stages || [];
  const requestWorkflowKey = getRequestWorkflowKey(request);
  const workflowName =
    selectedWorkflow?.name ||
    request?.workflowSnapshot?.name ||
    request?.workflowId?.name ||
    "Workflow";
  const workflowVersion =
    selectedWorkflow?.version ||
    request?.workflowVersion ||
    request?.workflowSnapshot?.version ||
    request?.workflowId?.version ||
    1;
  const criteriaToShow = resolvedCriteria || criteria;
  const combinations = criteriaOptions.combinations || [];
  const departmentOptions = criteria.unit
    ? [...new Set(combinations.filter((item) => item.unit === criteria.unit).map((item) => item.department).filter(Boolean))]
    : criteriaOptions.departments;
  const branchOptions = criteria.unit && criteria.department
    ? [...new Set(combinations
      .filter((item) => item.unit === criteria.unit && item.department === criteria.department)
      .map((item) => item.branch)
      .filter(Boolean))]
    : criteriaOptions.branches;
  const renderCriteriaOptions = (items) =>
    (items || []).map((item) => (
      <option key={item} value={item}>
        {item}
      </option>
    ));

  if (requestWorkflowKey) {
    return (
      <Box
        bg={previewBg}
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        p={4}
        mt={5}
      >
        <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
          Workflow Version
        </Text>
        {disabled ? (
          <HStack spacing={2} wrap="wrap">
            <Badge colorScheme="purple">{workflowName}</Badge>
            <Badge colorScheme="teal">v{workflowVersion}</Badge>
          </HStack>
        ) : (
          <VStack align="stretch" spacing={2}>
            <Select
              size="sm"
              value={selectedWorkflowId}
              onChange={(event) => onChange?.(event.target.value)}
              isDisabled={workflowLoading || workflowOptionsToShow.length === 0}
              placeholder="Select workflow version"
            >
              {workflowOptionsToShow.map((workflow) => {
                const optionKey = buildOptionKey(workflow);
                return (
                  <option key={optionKey} value={optionKey}>
                    {workflow.name} - v{workflow.version || 1} {workflow.isActive === true ? "(Active)" : workflow.isActive === false ? "(Inactive)" : ""}
                  </option>
                );
              })}
            </Select>
            <HStack spacing={2} wrap="wrap">
              <Badge colorScheme="purple">{workflowName}</Badge>
              <Badge colorScheme="teal">v{workflowVersion}</Badge>
            </HStack>
          </VStack>
        )}
      </Box>
    );
  }

  return (
    <Box bg={panelBg} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4} mt={5}>
      <FormLabel fontSize="sm" fontWeight="semibold" color="blue.700" _dark={{ color: "blue.200" }} mb={3}>
        Workflow Auto-Selection Criteria
      </FormLabel>
      <VStack align="stretch" spacing={4}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Unit</FormLabel>
            <Select
              value={criteria.unit}
              onChange={(event) => {
                setCriteria({ unit: event.target.value, department: "", branch: "" });
                setWorkflowOptions([]);
                setResolvedCriteria(null);
                onChange?.("");
              }}
              bg={previewBg}
              isDisabled={disabled}
              placeholder="Select unit"
            >
              {renderCriteriaOptions(criteriaOptions.units)}
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Department</FormLabel>
            <Select
              value={criteria.department}
              onChange={(event) => updateCriteria("department", event.target.value)}
              bg={previewBg}
              isDisabled={disabled || !criteria.unit}
              placeholder="Select department"
            >
              {renderCriteriaOptions(departmentOptions)}
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Branch</FormLabel>
            <Select
              value={criteria.branch}
              onChange={(event) => updateCriteria("branch", event.target.value)}
              bg={previewBg}
              isDisabled={disabled || !criteria.department}
              placeholder="Select branch"
            >
              {renderCriteriaOptions(branchOptions)}
            </Select>
          </FormControl>
        </SimpleGrid>

        {resolvedCriteria ? (
          <Box>
            <Badge colorScheme="blue" mr={2}>Resolved Unit: {criteriaToShow.unit}</Badge>
            <Badge colorScheme="blue" mr={2}>Department: {criteriaToShow.department}</Badge>
            <Badge colorScheme="blue">Branch: {criteriaToShow.branch}</Badge>
          </Box>
        ) : null}

        {workflowLoading ? (
          <Box>
            <Spinner size="sm" color="blue.500" />
            <Text as="span" ml={2} fontSize="sm" color="gray.600">
              Resolving workflow...
            </Text>
          </Box>
        ) : workflowOptionsToShow.length ? (
          <VStack align="stretch" spacing={3}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Workflow Version</FormLabel>
              <Select
                value={selectedWorkflowId}
                onChange={(event) => onChange?.(event.target.value)}
                bg={previewBg}
                isDisabled={disabled}
                placeholder="Select workflow version"
              >
                {workflowOptionsToShow.map((workflow) => {
                  const optionKey = buildOptionKey(workflow);
                  return (
                    <option key={optionKey} value={optionKey}>
                      {workflow.name} - v{workflow.version || 1} {workflow.isActive === true ? "(Active)" : workflow.isActive === false ? "(Inactive)" : ""}
                    </option>
                  );
                })}
              </Select>
            </FormControl>

            {selectedWorkflow?.description ? (
              <Text fontSize="xs" color="gray.600">
                {selectedWorkflow.description}
              </Text>
            ) : null}

            {approvalStages.length ? (
              <Box bg={previewBg} borderWidth="1px" borderColor="blue.100" borderRadius="md" p={3}>
                <Text fontSize="xs" fontWeight="bold" color="blue.700" _dark={{ color: "blue.200" }}>
                  Approval hierarchy preview
                </Text>
                <UnorderedList spacing={1} ml={4} mt={2}>
                  {approvalStages.map((stage, index) => (
                    <ListItem key={`${stage.stageName || "stage"}-${index}`}>
                      <Text as="span" fontSize="xs" fontWeight="semibold">
                        Stage {index + 1}: {stage.stageName || `Stage ${index + 1}`}
                      </Text>
                      <Text as="span" fontSize="xs" color="gray.600">
                        {" - "}
                        {getStageApproverSummary(stage)}
                      </Text>
                    </ListItem>
                  ))}
                </UnorderedList>
              </Box>
            ) : null}
          </VStack>
        ) : criteria.unit && criteria.department && criteria.branch ? (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">
              No workflow version found for this Risk Review criteria.
            </Text>
          </Alert>
        ) : (
          <Text fontSize="sm" color="gray.600">
            Select unit, department, and branch to load workflow versions.
          </Text>
        )}
      </VStack>
    </Box>
  );
};

export default RiskReviewWorkflowSelection;
